import { useEffect } from 'react';

import { RentalManagerListingCriteriaType } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyMultiEditWebApiHandler_multi } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { usePropertyEditMutation } from '../../hooks/usePropertyEditMutation';
import { useSetListingCriteriaMutation } from '../../hooks/useSetListingCriteriaMutation';
import Criterion from '../../model/ListingCriteria/Criterion';

import type { GetUserPropertyDetailsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { CriterionProps } from '../../model/ListingCriteria/Criterion';

type Props = {
  listingAlias: string;
  listingDetails: GetUserPropertyDetailsResponseWebDto | undefined;
  canAccessFastPost: boolean;
};

export const useListingInitialDefaults = ({ listingAlias, listingDetails, canAccessFastPost }: Props) => {
  const petDefaultsMutation = useSetListingCriteriaMutation();
  const fastPostDefaultsMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyMultiEditWebApiHandler_multi,
  });

  useEffect(() => {
    if (!listingDetails) return;

    const petsAllowedDto = listingDetails.petsAllowed;
    const isPetsAllowedUnset =
      petsAllowedDto != null &&
      petsAllowedDto.catsDto?.allowed === undefined &&
      petsAllowedDto.smallDogsDto?.allowed === undefined &&
      petsAllowedDto.largeDogsDto?.allowed === undefined;

    if (listingDetails?.listingCriteria?.isEnabled && isPetsAllowedUnset) {
      const currentCriteria = (listingDetails.listingCriteria?.criteria ?? []) as Array<CriterionProps>;
      const getCriterion = (type: string) => currentCriteria.find((c) => c.type === type);
      const initialRefundable = getCriterion(RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT)?.refundable;

      petDefaultsMutation.mutate({
        getParams: { listingAlias },
        postData: [
          Criterion({ type: RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT, criterion: '0', isEnabled: true }),
          Criterion({
            type: RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT,
            criterion: '0',
            isEnabled: true,
          }),
          Criterion({
            type: RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT,
            criterion: '0',
            isEnabled: true,
          }),
          Criterion({
            type: RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT,
            criterion: '',
            refundable: initialRefundable ?? null,
            isEnabled: true,
          }),
          Criterion({ type: RentalManagerListingCriteriaType.PET_POLICY_PET_RENT, criterion: '', isEnabled: true }),
        ],
      });
    }

    if (canAccessFastPost && !listingDetails.isMultifamily) {
      const isApplicationsUpdateRestricted = listingDetails.restrictions?.applications?.isUpdateRestricted ?? true;
      const defaults: Record<string, unknown> = {};

      if (!listingDetails.leaseDuration) defaults.leaseDuration = '1y';
      if (listingDetails.hideStreetAddress == null) defaults.hideStreetAddress = false;
      if (listingDetails.hidePhone == null) defaults.hidePhone = false;
      if (listingDetails.isApplicationsAccepted == null && !isApplicationsUpdateRestricted) {
        defaults.isApplicationsAccepted = true;
      }

      if (Object.keys(defaults).length > 0) {
        fastPostDefaultsMutation.mutate({
          postData: { property: { listingAlias, ...defaults } },
        });
      }
    }
  }, [listingDetails]); // eslint-disable-line react-hooks/exhaustive-deps
};
